import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Grid, Card, CardContent, Button, CircularProgress, CardMedia } from '@mui/material';
import { styled } from '@mui/material/styles';
import NavBar from './shared/NavBar';
import Footer from './shared/Footer';
import QuizIcon from '@mui/icons-material/Quiz';
import StarIcon from '@mui/icons-material/Star';
import config from '../config';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 30%, #2a2a2a 70%, #1a1a1a 100%)',
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(8),
}));

const QuizCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  border: '2px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.4s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 20px 50px rgba(255, 107, 53, 0.3)',
    border: '2px solid rgba(255, 107, 53, 0.5)',
    background: 'rgba(255, 255, 255, 0.08)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: 'linear-gradient(90deg, #FF6B35 0%, #FFB74D 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    zIndex: 1,
  },
  '&:hover::before': {
    opacity: 1,
  },
}));

const QuizBanner = styled(CardMedia)(({ theme }) => ({
  height: 200,
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  [theme.breakpoints.down('sm')]: {
    height: 150,
  },
}));

const FavouriteBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  background: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: '50%',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  '& svg': {
    color: '#FFD700',
    fontSize: '1.5rem',
  },
}));

const QuizTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const QuizDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '1rem',
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  lineHeight: 1.6,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
}));

const StartButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
  color: 'white',
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  borderRadius: '50px',
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #e85f2f 30%, #FFA726 70%)',
    transform: 'scale(1.05)',
    boxShadow: '0 6px 20px rgba(255, 107, 53, 0.6)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 24px',
    fontSize: '1rem',
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 'bold',
  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  textShadow: '0 0 30px rgba(255, 107, 53, 0.3)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '1.2rem',
  textAlign: 'center',
  marginBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    marginBottom: theme.spacing(4),
  },
}));

const AgeChip = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  padding: '6px 16px',
  borderRadius: '20px',
  background: 'rgba(6, 174, 213, 0.2)',
  border: '1px solid rgba(6, 174, 213, 0.4)',
  color: '#06AED5',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
}));

const Quizzes = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiBaseUrl = config.getApiUrl();

  useEffect(() => {
    fetchActiveQuizzes();
  }, []);

  const fetchActiveQuizzes = async () => {
    try {
      setLoading(true);
      setError(null);
      // console.log('Fetching quizzes from:', `${apiBaseUrl}/api/quizzes?activeOnly=true`);
      const response = await fetch(`${apiBaseUrl}/api/quizzes?activeOnly=true`);
      // console.log('Response status:', response.status);
      const data = await response.json();
      // console.log('Response data:', data);
      
      if (data.success) {
        setQuizzes(data.data || []);
      } else {
        console.error('API returned success=false:', data);
        setError('Failed to load quizzes');
      }
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      console.error('Error details:', err.message, err.stack);
      setError('Failed to load quizzes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizClick = (quizId) => {
    navigate(`/quiz/${quizId}`);
  };

  return (
    <>
      <NavBar />
      <PageContainer>
        <Container maxWidth="lg">
          {/* Header Section */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <QuizIcon sx={{ fontSize: '4rem', color: '#FF6B35', filter: 'drop-shadow(0 0 20px rgba(255, 107, 53, 0.5))' }} />
            </Box>
            <HeaderTitle>
              Ready to <Box component="span" sx={{ fontWeight: 900 }}>Test Your Knowledge?</Box>
            </HeaderTitle>
          </Box>

          {/* Quiz Cards */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#FF6B35' }} size={60} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.2rem' }}>
                {error}
              </Typography>
            </Box>
          ) : quizzes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.2rem' }}>
                No quizzes available at the moment. Check back soon!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {quizzes.map((quiz) => (
                <Grid item xs={12} sm={6} md={4} key={quiz._id}>
                  <QuizCard onClick={() => handleQuizClick(quiz._id)}>
                    {quiz.banner && (
                      <QuizBanner
                        image={quiz.banner}
                        title={quiz.name}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 12,
                            left: 12,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '999px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            zIndex: 2,
                          }}
                        >
                          ⏱️ 3 minutes
                        </Box>
                        {quiz.isFavourite && (
                          <FavouriteBadge>
                            <StarIcon />
                          </FavouriteBadge>
                        )}
                      </QuizBanner>
                    )}
                    
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 }, textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                        <QuizTitle sx={{ mb: 0, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                          {quiz.name}
                        </QuizTitle>
                      </Box>
                      
                      <QuizDescription sx={{ mb: 'auto', pb: 2 }}>
                        {quiz.questions?.length || 0} Questions
                      </QuizDescription>
                      
                      <StartButton>
                        Start Quiz →
                      </StartButton>
                    </CardContent>
                  </QuizCard>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Info Section */}
          <Box sx={{ 
            mt: 8, 
            p: 4, 
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center'
          }}>
            <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
              Why Take Our Quizzes?
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.95rem' }}>
                  📚 <strong>Learn While Playing</strong><br />
                  Fun and educational questions
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.95rem' }}>
                  🏆 <strong>Track Progress</strong><br />
                  See how much you've learned
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.95rem' }}>
                  🎯 <strong>Age-Appropriate</strong><br />
                  Questions tailored to your level
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </PageContainer>
      <Footer />
    </>
  );
};

export default Quizzes;
